import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Pencil, Printer } from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatDisplayDate } from '@/utils/date';
import { pcmpProposalDetailQueryOptions } from '../api/pcmpProposals.api';
import { PCMP_HOLD_STAGE_LABELS } from '../constants/pcmpHold.constants';
import { PCMP_LANE_TONE_COLOR } from '../constants/pcmpProposal.constants';
import { PcmpMarketingActionButtons } from '../components/PcmpMarketingActions/PcmpMarketingActionButtons';
import { PcmpAccountsActionButtons } from '../components/PcmpAccountsActions/PcmpAccountsActionButtons';
import { PcmpAdminRejectButton } from '../components/PcmpAdminReject/PcmpAdminRejectButton';
import { PcmpRequestActButtons } from '../components/PcmpRequested/PcmpRequestActButtons';
import { PcmpRequestButton } from '../components/PcmpRequests/PcmpRequestButton';
import { PcmpHoldActionButtons } from '../components/PcmpHoldActions/PcmpHoldActionButtons';
import { PcmpCorrectionActionButtons } from '../components/PcmpCorrection/PcmpCorrectionActionButtons';
import { PcmpConfirmDisbursementButton } from '../components/PcmpDisbursementAck/PcmpConfirmDisbursementButton';
import { PcmpPayoutAcksCard } from '../components/PcmpDisbursementAck/PcmpPayoutAcksCard';
import { PcmpVerifyDisbursementButton } from '../components/PcmpDisbursementAck/PcmpVerifyDisbursementButton';
import { PcmpReturnPaymentButton } from '../components/PcmpReturn/PcmpReturnPaymentButton';
import { PcmpMisActionButtons } from '../components/PcmpMisActions/PcmpMisActionButtons';
import { PcmpAttachmentsCard } from '../components/PcmpProposalDetail/PcmpAttachmentsCard';
import { PcmpDisbursementProofCard } from '../components/PcmpProposalDetail/PcmpDisbursementProofCard';
import { PcmpCalculationCard } from '../components/PcmpProposalDetail/PcmpCalculationCard';
import { PcmpInstituteCard } from '../components/PcmpProposalDetail/PcmpInstituteCard';
import { PcmpInstituteProposalsCard } from '../components/PcmpProposalDetail/PcmpInstituteProposalsCard';
import { PcmpLaneStatusStrip } from '../components/PcmpProposalDetail/PcmpLaneStatusStrip';
import { PcmpOfficerCard } from '../components/PcmpProposalDetail/PcmpOfficerCard';
import { PcmpPaymentCard } from '../components/PcmpProposalDetail/PcmpPaymentCard';
import { PcmpPaymentSourceCard } from '../components/PcmpProposalDetail/PcmpPaymentSourceCard';
import { PcmpProposalBanners } from '../components/PcmpProposalDetail/PcmpProposalBanners';
import { PcmpProposalSection } from '../components/PcmpProposalDetail/PcmpProposalSection';
import { PcmpProposalTimeline } from '../components/PcmpProposalDetail/PcmpProposalTimeline';
import { PcmpStatusPill } from '../components/PcmpProposalDetail/PcmpStatusPill';
import { PcmpStudentSummaryCard } from '../components/PcmpProposalDetail/PcmpStudentSummaryCard';
import { PcmpTeacherCard } from '../components/PcmpProposalDetail/PcmpTeacherCard';
import { PcmpInstituteEditDialog } from '../components/PcmpProposalEdit/PcmpInstituteEditDialog';
import { PcmpPaymentEditDialog } from '../components/PcmpProposalEdit/PcmpPaymentEditDialog';
import { PcmpPaymentSourceDialog } from '../components/PcmpProposalEdit/PcmpPaymentSourceDialog';
import { PcmpStudentEditDialog } from '../components/PcmpProposalEdit/PcmpStudentEditDialog';
import { PcmpTeacherEditDialog } from '../components/PcmpProposalEdit/PcmpTeacherEditDialog';
import type { PcmpChannel, PcmpProposalEditSection } from '../types/pcmpProposal.types';

export function PcmpProposalDetailPage() {
  const { proposalId } = useParams({ from: '/_auth/pcmp/proposals/$proposalId/' });
  const router = useRouter();
  const [editingSection, setEditingSection] = useState<PcmpProposalEditSection | null>(null);
  const [sourceTarget, setSourceTarget] = useState<PcmpChannel | null>(null);

  const {
    data: proposal,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(pcmpProposalDetailQueryOptions(proposalId));

  function goBack() {
    if (router.history.canGoBack()) router.history.back();
    else void router.navigate({ to: '/' });
  }

  const backButton = (
    <Button type="button" variant="outline" size="sm" onClick={goBack} className="w-fit gap-1.5">
      <ArrowLeft className="size-3.5" />
      Back
    </Button>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <PageLoader />
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <ErrorState
          error={error}
          fallbackTitle="Could not load this proposal"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  const overall = proposal.status.lanes.overall;
  const { capabilities } = proposal;
  const closeEdit = () => setEditingSection(null);

  // Buttons follow the API's capabilities only; the rules stay server-side.
  const editButton = (section: PcmpProposalEditSection) =>
    capabilities.canEdit ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setEditingSection(section)}
        className="gap-1.5"
      >
        <Pencil className="size-3.5" />
        Edit
      </Button>
    ) : null;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {backButton}
          <h1 className="text-2xl font-semibold">{proposal.refNo}</h1>
          <PcmpStatusPill tone="neutral">{proposal.proposalType}</PcmpStatusPill>
          <PcmpStatusPill tone={PCMP_LANE_TONE_COLOR[overall.tone]}>
            {overall.text ?? proposal.status.status.label}
          </PcmpStatusPill>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-col text-sm text-muted-foreground lg:items-end">
            <span className="font-medium text-foreground">{proposal.project.displayName}</span>
            <span>Proposed {formatDisplayDate(proposal.proposalDate)}</span>
          </div>
          <Link
            to="/pcmp/proposals/$proposalId/print"
            params={{ proposalId }}
            target="_blank"
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
          >
            <Printer className="size-3.5" />
            Print
          </Link>
          <PcmpMarketingActionButtons
            proposalId={proposalId}
            refNo={proposal.refNo}
            approve={
              capabilities.canMktApprove && capabilities.mktApproveKind
                ? { kind: capabilities.mktApproveKind, levelNo: capabilities.mktApproveLevelNo }
                : null
            }
            canReject={capabilities.canMktReject}
            canRejectInstitute={capabilities.canRejectInstitute}
            explanation={
              capabilities.canSubmitMktExplanation
                ? { misReasonLabel: proposal.status.lanes.mis.sub }
                : null
            }
            canDisburse={capabilities.canMktDisburse}
          />
          {capabilities.canMisDecide ? (
            <PcmpMisActionButtons proposalId={proposalId} refNo={proposal.refNo} />
          ) : null}
          <PcmpAccountsActionButtons
            proposalId={proposalId}
            refNo={proposal.refNo}
            fail={
              capabilities.canAccountsFail ? { stage: capabilities.accountsFailureStage } : null
            }
            canReturn={capabilities.canAccountsReturn}
          />
          <PcmpReturnPaymentButton
            proposalId={proposalId}
            refNo={proposal.refNo}
            channel="MARKETING"
            canReturn={capabilities.canMarketingReturn}
          />
          <PcmpConfirmDisbursementButton
            proposalId={proposalId}
            refNo={proposal.refNo}
            canConfirm={capabilities.canConfirmDisbursement}
          />
          <PcmpVerifyDisbursementButton
            proposalId={proposalId}
            refNo={proposal.refNo}
            payout={proposal.payout}
            canVerify={capabilities.canVerifyDisbursement}
          />
          {capabilities.canCorrect ? (
            <PcmpCorrectionActionButtons
              proposalId={proposalId}
              paymentType={proposal.payment.paymentType}
            />
          ) : null}
          <PcmpHoldActionButtons
            proposalId={proposalId}
            refNo={proposal.refNo}
            canHold={capabilities.canHold}
            canRelease={capabilities.canRelease}
            holdStageLabel={
              capabilities.holdStage ? PCMP_HOLD_STAGE_LABELS[capabilities.holdStage] : null
            }
            hold={proposal.hold}
          />
          <PcmpRequestButton
            proposalId={proposalId}
            refNo={proposal.refNo}
            proposedAmount={proposal.payment.proposedAmount}
            paymentSource={proposal.paymentSource}
            canRequest={capabilities.canRequest}
          />
          <PcmpRequestActButtons
            proposalId={proposalId}
            refNo={proposal.refNo}
            proposedAmount={proposal.payment.proposedAmount}
            canAct={capabilities.canActOnRequest}
            canProcess={proposal.openRequest?.status === 'PENDING'}
            approveBlockedReason={capabilities.requestApproveBlockedReason}
            categoryCode={proposal.openRequest?.categoryCode}
          />
          <PcmpAdminRejectButton
            proposalId={proposalId}
            refNo={proposal.refNo}
            canReject={capabilities.canAdminReject}
            stage={capabilities.adminRejectStage}
          />
        </div>
      </div>

      {!capabilities.canRequest && capabilities.requestBlockedReason ? (
        <p className="text-sm text-muted-foreground">
          Requesting is not available: {capabilities.requestBlockedReason}
        </p>
      ) : null}

      {!capabilities.canEdit && capabilities.editBlockedReason ? (
        <p className="text-sm text-muted-foreground">
          Editing is not available: {capabilities.editBlockedReason}
        </p>
      ) : null}

      <PcmpProposalBanners proposal={proposal} />

      <PcmpProposalSection title="Status">
        <PcmpLaneStatusStrip status={proposal.status} />
      </PcmpProposalSection>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <PcmpOfficerCard mo={proposal.mo} />
        <PcmpInstituteCard institute={proposal.institute} actions={editButton('institute')} />
      </div>

      <PcmpCalculationCard calculation={proposal.calculation} />
      <PcmpStudentSummaryCard
        students={proposal.students}
        calculation={proposal.calculation}
        actions={editButton('students')}
      />
      <PcmpTeacherCard
        teacher={proposal.teacher}
        proposedAmount={proposal.payment.proposedAmount}
        isSubmissionRejected={proposal.institute.submissionStatus === 'REJECTED'}
        actions={editButton('teacher')}
      />
      <PcmpInstituteProposalsCard proposals={proposal.instituteProposals} />
      <PcmpPaymentCard payment={proposal.payment} actions={editButton('payment')} />
      <PcmpPaymentSourceCard
        paymentSource={proposal.paymentSource}
        canChange={capabilities.canChangePaymentSource}
        blockedReason={capabilities.paymentSourceBlockedReason}
        onChangeRequest={setSourceTarget}
      />
      {proposal.disbursementProof ? (
        <PcmpDisbursementProofCard proof={proposal.disbursementProof} />
      ) : null}
      <PcmpPayoutAcksCard payout={proposal.payout} moName={proposal.mo.fullName} />
      <PcmpAttachmentsCard attachments={proposal.attachments} />
      <PcmpProposalTimeline timeline={proposal.timeline} students={proposal.students} />

      {/* Mounted only while open, so each opens on the latest details. */}
      {editingSection === 'institute' ? (
        <PcmpInstituteEditDialog proposal={proposal} onClose={closeEdit} />
      ) : null}
      {editingSection === 'students' ? (
        <PcmpStudentEditDialog proposal={proposal} onClose={closeEdit} />
      ) : null}
      {editingSection === 'teacher' ? (
        <PcmpTeacherEditDialog proposal={proposal} onClose={closeEdit} />
      ) : null}
      {editingSection === 'payment' ? (
        <PcmpPaymentEditDialog proposal={proposal} onClose={closeEdit} />
      ) : null}
      {sourceTarget ? (
        <PcmpPaymentSourceDialog
          proposal={proposal}
          target={sourceTarget}
          onClose={() => setSourceTarget(null)}
        />
      ) : null}
    </div>
  );
}
